/*
 * seminar-participant-form.js
 * ---------------------------------------------------------------------------
 * Product-page behaviour for the "second participant" capture form used on
 * seminar products.
 *
 * Responsibilities:
 *   1. Cap the product quantity at the configured maximum (default 2).
 *   2. Show the participant fields only when the quantity reaches 2, and
 *      enable/disable them so they are submitted only when relevant.
 *   3. Validate the required fields before the add-to-cart request fires,
 *      blocking submission (and showing a message) when invalid.
 *
 * The element wraps inputs that are associated to the product form via the
 * HTML5 `form` attribute, so the inputs post as line item properties even
 * though they live outside the <form> element.
 */
if (!customElements.get('seminar-participant-form')) {
  customElements.define(
    'seminar-participant-form',
    class SeminarParticipantForm extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.form = document.getElementById(this.formId);
        this.sectionId = this.dataset.sectionId;
        this.maxQuantity = parseInt(this.dataset.maxQuantity, 10) || 2;
        this.triggerQuantity = parseInt(this.dataset.triggerQuantity, 10) || 2;

        this.fieldsWrapper = this.querySelector('[data-seminar-fields]');
        this.fields = Array.from(this.querySelectorAll('[data-seminar-field]'));
        this.errorEl = this.querySelector('[data-seminar-error]');

        // Quantity input is associated to the form by id, not nested here.
        this.quantityInput =
          document.getElementById('Quantity-' + this.sectionId) ||
          (this.form && this.form.querySelector('input[name="quantity"]'));

        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);

        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', this.onQuantityChange);
          this.quantityInput.addEventListener('input', this.onQuantityChange);
        }

        // Capture phase so we run before product-form.js's submit handler.
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitCapture, true);
        }

        // Clear an error as soon as the customer edits a field.
        this.fields.forEach((field) => {
          field.addEventListener('input', () => this.clearError());
          field.addEventListener('change', () => this.clearError());
        });

        this.enforceMax();
        this.toggleFields();
      }

      get quantity() {
        if (!this.quantityInput) return 1;
        return parseInt(this.quantityInput.value, 10) || 1;
      }

      onQuantityChange() {
        this.enforceMax();
        this.toggleFields();
      }

      /* Keep the quantity within [min, maxQuantity]. */
      enforceMax() {
        if (!this.quantityInput) return;
        this.quantityInput.setAttribute('max', this.maxQuantity);
        this.quantityInput.dataset.max = this.maxQuantity;
        if (this.quantity > this.maxQuantity) {
          this.quantityInput.value = this.maxQuantity;
        }
      }

      /* Show + enable the fields only at/above the trigger quantity. */
      toggleFields() {
        const active = this.quantity >= this.triggerQuantity;
        if (this.fieldsWrapper) this.fieldsWrapper.hidden = !active;
        this.fields.forEach((field) => {
          field.disabled = !active;
        });
        if (!active) this.clearError();
      }

      /*
       * Validate before add-to-cart. Returns nothing; calls preventDefault +
       * stopImmediatePropagation to halt the product-form.js handler when the
       * form is invalid.
       */
      onSubmitCapture(event) {
        if (this.quantity < this.triggerQuantity) return;

        const errors = this.validate();
        if (errors.length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showErrors(errors);
          if (errors[0].field) errors[0].field.focus();
        }
      }

      validate() {
        const errors = [];
        this.fields.forEach((field) => {
          const label = this.labelFor(field);
          const value = (field.value || '').trim();
          const required = field.dataset.required === 'true';

          if (required && value === '') {
            errors.push({ field, message: label + 'を入力してください。' });
            field.setAttribute('aria-invalid', 'true');
            return;
          }

          if (value !== '' && field.dataset.validate === 'email' && !this.isEmail(value)) {
            errors.push({ field, message: 'メールアドレスの形式が正しくありません。' });
            field.setAttribute('aria-invalid', 'true');
            return;
          }

          if (value !== '' && field.dataset.validate === 'tel' && !this.isTel(value)) {
            errors.push({ field, message: '電話番号は数字（ハイフン可）でご入力ください。' });
            field.setAttribute('aria-invalid', 'true');
            return;
          }

          field.removeAttribute('aria-invalid');
        });
        return errors;
      }

      labelFor(field) {
        const label = this.querySelector('label[for="' + field.id + '"]');
        if (!label) return '項目';
        return label.textContent.replace('*', '').trim();
      }

      isEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      isTel(value) {
        return /^[0-9０-９+\-（）()\s]{8,}$/.test(value);
      }

      showErrors(errors) {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        errors.forEach((e) => {
          const p = document.createElement('p');
          p.textContent = e.message;
          this.errorEl.appendChild(p);
        });
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }
  );
}
